<script setup lang="ts">
import MmDialog from '../base/MmDialog.vue'
import MmField from '../base/MmField.vue'
import MmInput from '../base/MmInput.vue'
import MmButton from '../base/MmButton.vue'
import MmIcon from '../base/MmIcon.vue'
import { useCreator } from '../creator/useCreator'
import { nameLength, profileErrorKey } from '../creator/contracts.mjs'
import { useShareIntent } from '../creator/incentives/useShareIntent'
import { generateFullSlug } from '~/utils/string'
import { trackEvent } from '~/utils/analytics'
import { createGrowthEventId, getGrowthEventAnonymousId, getGrowthEventSessionId } from '~/utils/growthEvents'
import { sendShareGrowthEvent } from '~/utils/shareGrowth'
import { useToast } from '~/composables/useToast'
const props = defineProps<{ modelValue: boolean; song: { id: string; title?: string }; surface: string }>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()
const { t } = useI18n(), shareIntent = useShareIntent()
const { profile, identity, load } = useCreator()
const toast = useToast()
const config = useRuntimeConfig()
const dialog = ref<InstanceType<typeof MmDialog> | null>(null)
const recipient = ref(''), sender = ref(''), busy = ref(false)
const recipientError = ref(''), senderError = ref(''), error = ref(''), manualLink = ref('')
const cleanName = (value: string) => value.trim().replace(/\s+/gu, ' ')
const count = computed(() => nameLength(cleanName(recipient.value)))
const senderCount = computed(() => nameLength(cleanName(sender.value)))
let senderEdited = false
let request = 0
watch(() => props.modelValue, open => {
  ++request; busy.value = false; shareIntent.finish()
  if (!open) return
  recipient.value = ''; sender.value = profile.value?.display_name || ''; senderEdited = false
  recipientError.value = senderError.value = error.value = manualLink.value = ''
  void load('profile')
}, { immediate: true })
watch(() => profile.value?.display_name, name => { if (props.modelValue && !senderEdited) sender.value = name || '' })
watch(identity, () => emit('update:modelValue', false))
watch([shareIntent.locale, () => props.song.id], () => { request++; busy.value=false; error.value=''; manualLink.value=''; shareIntent.finish() })
function restoreParameters() { const params=shareIntent.state.intent?.params; if(params) { recipient.value=params.recipient_name||'';sender.value=params.sender_name||'';error.value='' } }
watch(recipient, () => { recipientError.value = ''; error.value = '' })
watch(sender, () => { senderError.value = ''; error.value = '' })
const focusField = async (name: string) => { await nextTick(); dialog.value?.dialog?.querySelector<HTMLInputElement>(`[name="${name}"]`)?.focus() }
const analytics = (name: string) => trackEvent(name, { surface: props.surface, song_id: props.song.id, share_kind: 'gift' })
async function submit() {
  if (busy.value) return
  const recipientName = cleanName(recipient.value), senderName = cleanName(sender.value)
  recipientError.value = senderError.value = error.value = ''
  if (!count.value) recipientError.value = t('share.gift.required')
  else if (count.value > 24) recipientError.value = t('creator.errors.name_too_long')
  if (senderCount.value > 24) senderError.value = t('creator.errors.name_too_long')
  if (recipientError.value || senderError.value) { await focusField(recipientError.value ? 'recipient' : 'sender'); return }
  busy.value = true
  const attempt = ++request, song = { ...props.song }, owner = identity.value, context = shareIntent.version()
  const current = () => attempt === request && props.modelValue && owner === identity.value && shareIntent.isCurrent(context)
  try {
    const response = await shareIntent.generate({ id: song.id, recipient_name: recipientName, ...(senderName ? { sender_name: senderName } : {}) })
    if (!current()) return
    if (response.code !== 200 || !response.data?.token) throw { errorCode: response.data?.error_code }
    analytics('gift_link_create')
    const url = `${String(config.public.siteUrl || 'https://musicmint.ai').replace(/\/$/, '')}/share/${generateFullSlug(response.data.token, song.title || '')}`
    const text = t('share.gift.shareText', { name: recipientName, link: url })
    let shared = false
    try {
      if (navigator.share) { await navigator.share({ text }); if (!current()) return; shared = true }
      else { await navigator.clipboard.writeText(url); if (!current()) return; toast.success(t('ux.copied')); shared = true }
    } catch (e) {
      if (!current() || (e as Error).name === 'AbortError') return
      try { await navigator.clipboard.writeText(url); if (!current()) return; toast.success(t('ux.copied')); shared = true }
      catch { if (current()) {manualLink.value = url; error.value = t('ux.shareCopyFailed')} }
    }
    if (!shared) return
    analytics('gift_link_share')
    void sendShareGrowthEvent({ event_id: createGrowthEventId(), request_id: createGrowthEventId(), session_id: getGrowthEventSessionId(), anonymous_id: getGrowthEventAnonymousId(), content_type: 'music', content_id: song.id, event_type: 'share', surface: props.surface === 'song_page' ? 'song_page' : 'player', metadata: { share_kind: 'gift', source_surface: props.surface } })
    emit('update:modelValue', false)
  } catch (e: any) {
    if (!current()) return
    const code = e?.errorCode || e?.response?.data?.data?.error_code || ''
    const message = t(profileErrorKey({ errorCode: code }))
    if (code.startsWith('name_')) {
      // The API supplies a name error, but no field identifier. Mark each
      // submitted name so a sender rejection is never attributed only to the recipient.
      recipientError.value = message
      if (senderName) senderError.value = message
      await focusField('recipient')
    } else error.value = t(shareIntent.errorKey(e))
  } finally { if (attempt === request) busy.value = false }
}
onBeforeUnmount(() => { ++request; shareIntent.finish() })
</script>
<template>
  <MmDialog ref="dialog" :model-value="modelValue" :title="t('share.gift.dialogTitle')" display-title @update:model-value="emit('update:modelValue', $event)">
    <form class="gift-form" novalidate @submit.prevent="submit">
      <MmField id="gift-recipient" :label="t('share.gift.theirName')" :count="count" :max="24" :error="recipientError"><MmInput v-model="recipient" name="recipient" required :disabled="busy" autocomplete="off" /></MmField>
      <MmField id="gift-sender" :label="t('share.gift.from')" :hint="t('share.gift.senderHint')" :count="senderCount" :max="24" :error="senderError"><MmInput v-model="sender" name="sender" :disabled="busy" autocomplete="off" @input="senderEdited = true" /></MmField>
      <p class="gift-preview"><MmIcon name="gift" :size="18" /><span>{{ t('share.gift.preview', { recipient: cleanName(recipient) || t('share.gift.recipientPreview'), sender: cleanName(sender) || t('share.gift.friend') }) }}</span></p>
      <p v-if="error" class="error-text" role="alert">{{ error }}</p><MmButton v-if="shareIntent.state.error === 'conflict' && shareIntent.state.intent" variant="ghost" @click="restoreParameters">{{ t('incShare.restore') }}</MmButton>
      <MmField v-if="manualLink" :label="t('ux.shareLink')"><MmInput :model-value="manualLink" readonly @focus="($event.target as HTMLInputElement).select()" /></MmField>
      <MmButton type="submit" size="lg" class="btn-block" :loading="busy" :disabled="busy"><MmIcon name="gift" :size="20" />{{ t('share.gift.createLink') }}</MmButton>
    </form>
  </MmDialog>
</template>
<style scoped>
.gift-form { display: grid; gap: var(--s-5); }
.gift-form :deep(.field-label) { font: 600 var(--t-small)/1.5 var(--font-ui); text-transform: none; letter-spacing: normal; }
.gift-form :deep(.input) { width: 100%; min-height: 44px; font-size: 16px; }
.gift-preview { display: flex; align-items: center; gap: var(--s-2); width: fit-content; max-width: 100%; padding: var(--s-2) var(--s-3); border-radius: var(--r-pill); background: var(--accent-soft); color: var(--accent-text); font-size: var(--t-small); overflow-wrap: anywhere; }
.gift-preview .ic { flex: none; }
</style>
